/**
 * The craft-study feature (M8 plan MC-f3): registers `pattern.study` and `pattern.record` on the craft hook
 * (`craft/hook.ts`), so `UiActions.craft` runs them.
 *
 * - **Study** acts at the docked station (it must offer outfitting): `craft/unlocks.ts study` consumes 3 Intact hold
 *   components of the pattern's maker and writes the rung into `career.patterns.unlocked`. Without a `rung` it
 *   studies the first rung not yet unlocked. Success raises a station notice naming the modules it opens ("Pattern open: PDC Mk II (Hot) and Pulse Laser Mk II (Hot) ...", PF-5, PG-2 R2-9).
 * - **Record** writes the rungs standing and blueprint fragments open now into the store (`recordRouteUnlocks`), so
 *   they stay unlocked when standing later falls. The feature runs it itself whenever the published Career changes
 *   and a rung is due (deferred to a microtask, never inside the store's notification); it commits without clearing
 *   the inline error, and is a no-op when nothing is new.
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import { logger } from '../../core/logger'
import type { Career } from '../../career/types'
import { registerCraftHandler, type CraftCommandMap, type CraftHandler } from '../../craft/hook'
import { nextStudyRung, recordRouteUnlocks, study, unlockData, type UnlockData } from '../../craft/unlocks'
import type { CmdOutcome } from '../../ui/contracts'
import { studyResultLine } from '../../ui/models/outfitting'

/** After the career runtime (the registry is global, so the order only places it in the list). */
const ORDER = FEATURE_ORDER.career + 1

const NOT_DOCKED_TEXT = 'Dock at an outfitting station to study a pattern.'

/** The two handlers (exported for the tests; the feature registers them). */
export function createStudyHandlers(data: () => UnlockData = unlockData): {
  study: CraftHandler<CraftCommandMap['pattern.study']>
  record: CraftHandler<CraftCommandMap['pattern.record']>
} {
  return {
    study(cmd, ctx): CmdOutcome {
      const here = ctx.station
      if (here === null) return ctx.refuse({ code: 'serviceUnavailable', message: NOT_DOCKED_TEXT })
      const career = ctx.career()
      const unlocks = data()
      const rung = cmd.rung ?? nextStudyRung(career, cmd.pattern, unlocks) ?? 'mk3'
      const r = study(career, ctx.data, here, cmd.pattern, rung, unlocks)
      if (!r.ok) return ctx.refuse(r.error)
      // PF-5: the station notice confirms the pattern opened (the Study row just goes).
      ctx.commit(r.value, [...r.effects, { kind: 'resultLine', text: studyResultLine(cmd.pattern, rung, ctx.data, unlocks) }])
      return ctx.done()
    },
    record(_cmd, ctx): CmdOutcome {
      const career = ctx.career()
      const next = recordRouteUnlocks(career, data())
      if (next !== career) ctx.commit(next)
      return { ok: true }
    },
  }
}

/** Whether `career` has a standing or fragment rung not yet written into the store. */
export function routeUnlocksDue(career: Career, data: UnlockData = unlockData()): boolean {
  return recordRouteUnlocks(career, data) !== career
}

let unregister: (() => void)[] = []

const feature: GameFeature = {
  name: 'craftStudy',
  order: ORDER,
  init(ctx: GameContext): void {
    const handlers = createStudyHandlers()
    unregister = [registerCraftHandler('pattern.study', handlers.study), registerCraftHandler('pattern.record', handlers.record)]
    // The career runtime provides `m2` in its own init (career and Reach modes); the sandbox has none.
    const m2 = ctx.services.maybe('m2')
    if (m2 === undefined) return
    let queued = false
    const off = m2.ui.subscribe((s, prev) => {
      const career = s.career
      if (queued || career === null || career === prev.career || !routeUnlocksDue(career)) return
      queued = true
      queueMicrotask(() => {
        queued = false
        const craft = m2.ui.getState().actions.craft
        if (craft === undefined || m2.ui.getState().career === null) return
        const out = craft({ kind: 'pattern.record' })
        if (!out.ok) logger.warn(`pattern unlocks: record refused (${out.error.code})`)
      })
    })
    unregister.push(off)
  },
  dispose(): void {
    for (const off of unregister) off()
    unregister = []
  },
}

export default feature
